import * as Yup from 'yup';
import { KenticoValidatorUtils } from "../kentico/KenticoUtils";
import * as Moment from 'moment';
import * as Iban from 'iban';
import { FileNameHelpers } from '../helpers/FileNameHelpers';
import { getResourceString } from '../kentico/resource-strings';
import { FormLabelHelpers } from '../formcontrols/helpers/FormLabelHelpers';
import { StringHelpers } from '../helpers/StringHelpers';

function hasOnlyNumbers(valueString : string) : boolean {    
    let loop = 0;
    while (loop < valueString.length) {
      if ( valueString[loop] < '0' || valueString[loop] > '9') {
        return false;
      }
      loop++;
    }

    return true;
  }

export class ValidationHelpers {

    static addKenticoValidator(formFieldDefinition : any, validationSchemaShape: any, fieldDefinitions : any, formikBag : any) {
      
        if (formFieldDefinition.validation && formFieldDefinition.validation.required === undefined) {
          if (formikBag.values[formFieldDefinition.name] === "") {
            return true;
          }
        }

        let kenticoRules = formFieldDefinition.validation["kenticoRules"];
        kenticoRules.rules.map((rule : any) => {

          validationSchemaShape[formFieldDefinition.name] = Yup.mixed().
          test('kenticoValidatie', rule["error"], function (value) {
            let valueHelper : any = this;
            let fieldsMapper = {};
            fieldDefinitions.map((fieldDefinition) => {
              if (fieldDefinition.id !== undefined) {
                Object.defineProperty(fieldsMapper, fieldDefinition.id, {
                  get: () => {
                    return {
                      Value: formikBag.values[fieldDefinition.name]
                    };
                  }
                });
              }
            });

            if (!rule.validationFunction( fieldsMapper,
              value,  
              KenticoValidatorUtils)) {
              return false;
            }
            return true;
      
          });
        });
    }

    static addLidNrValidator(formFieldDefinition : any, validationSchemaShape: any, dataType : any) {
        validationSchemaShape[formFieldDefinition.name] = Yup.string().typeError(dataType.error)
        .transform(function(value, originalvalue) {
          if (originalvalue == "") {
            return value;
          }
          let left2Numbers = originalvalue.substr(0, 2);
          if (hasOnlyNumbers(originalvalue) && (['01', '02', '03', '15', '17', '30'].indexOf(left2Numbers) >= 0)) {
            return value;
          }
          return new Date('');
        });
    }

    static addNumberValidator(formFieldDefinition : any, validationSchemaShape: any, dataType : any) {

      let schema = Yup.lazy((value) => {
        if (value === '') {
          return Yup.string();
        }          
        let numberSchema = Yup.number();

        if (!!dataType.allowDecimals) {
          numberSchema = numberSchema.transform((_, value) => {
            if (value.includes('.')) {
              return null;
            }
            return +value.replace(/,/, '.');
          });
        }

        if (dataType.min !== undefined && dataType.max !== undefined) {          
          numberSchema = numberSchema
            .min(dataType.min, dataType.minmaxerror)
            .max(dataType.max, dataType.minmaxerror)
            .typeError(dataType.error);
        } else {
          numberSchema = numberSchema.typeError(dataType.error);
        }
        return numberSchema;
      });
    
      validationSchemaShape[formFieldDefinition.name] = schema;
    }

    static addMaxAgeCheckValidator(formFieldDefinition : any, validationSchemaShape: any, dataType : any) {

      validationSchemaShape[formFieldDefinition.name] = Yup.mixed().test("geboortedatum lid worden formulier", "default error", function (value) {
        let date = Moment(value, ["YYYY-MM-DDTHH-mm-ss", "DD-MM-YYYY"]);
        
        if (date.isValid()) {
          if (typeof dataType.maxage !== "undefined") {
            var age = Moment().diff(date, 'years');
            if (dataType.maxage && dataType.maxage > 0 && age < dataType.maxage &&
              dataType.minage && dataType.minage > 0 && age >= dataType.minage) {
                return this.createError({ path: "", message: dataType.error});
            } else
            if ((dataType.minage && dataType.minage > 0 && age < dataType.minage) || (date.year() < parseInt(dataType.minyear))) {
              return this.createError({ path: "", message: dataType.minageerror});
            }
            
          }
          return true;
        } else {
          const isRequired = formFieldDefinition.validation['required'] !== undefined;
          if (isRequired && value === "") {
            return this.createError({ 
              path: "", 
              message: StringHelpers.formFieldRequiredFieldErrorMessage(FormLabelHelpers.getLabelWithoutAsterisk(formFieldDefinition.label))
            });
          }
        }                           
      });
      
    }

    static addDateValidator(formFieldDefinition : any, validationSchemaShape: any, dataType : any) {

        let existingSchema;
        if (validationSchemaShape[formFieldDefinition.name]) {
          existingSchema = validationSchemaShape[formFieldDefinition.name];
        }

        validationSchemaShape[formFieldDefinition.name] = Yup.date().typeError(dataType.error)
        .transform(function(value, originalvalue) {

          let isRequired = formFieldDefinition.validation['required'] !== undefined;

          if (!isRequired && (originalvalue === "" || originalvalue == "0001-01-01T00:00:00")) {
            // dit is nodig om lege datum velden (die niet verplicht zijn) mogelijk te maken
            return new Date();
          }
          if (this.isType(originalvalue)) return value;
          
          let date = Moment(originalvalue, ["YYYY-MM-DDTHH-mm-ss", "DD-MM-YYYY"]);
          
          if (date.isValid()) {
            if (typeof dataType.minage !== "undefined") {
              var age = Moment().diff(date, 'years');
              if (dataType.minage && dataType.minage > 0 && age < dataType.minage) {
                return new Date('');
              }
            }

            if (typeof dataType.minyear !== "undefined") {
              if (date.year() < parseInt(dataType.minyear)) {
                return new Date('');
              }
            }

            return date.toDate();
          }
          return new Date('');
        });              
    }

    static addIbanValidator(formFieldDefinition : any, validationSchemaShape: any, dataType : any) {
        validationSchemaShape[formFieldDefinition.name] = Yup.string().typeError(dataType.error)
        .transform(function(value, originalvalue) {
            if (Iban.isValid(originalvalue)) {
              return originalvalue;
            }
            return new Date(''); // hack to return invalid value
        });
    }

    static addZipcodeValidator(formFieldDefinition : any, validationSchemaShape: any, dataType : any) {
        validationSchemaShape[formFieldDefinition.name] = Yup.string().typeError(dataType.error)
        .transform(function(value, originalvalue) {
          if (originalvalue == "") {
            return value;
          }

          var regex = /^[1-9][0-9]{3}(?!sa|sd|ss)[a-z]{2}$/i;

          if (regex.test(originalvalue) && originalvalue.length == 6) {
            return originalvalue;
          }
          return new Date(''); // hack to return invalid value
        });
    }

    static addCheckboxValidator(formFieldDefinition : any, validationSchemaShape: any) {
        validationSchemaShape[formFieldDefinition.name] = validationSchemaShape[formFieldDefinition.name].matches(/(true)/, formFieldDefinition.validation["required"].error);
    }

    static addFileUploadValidator(formFieldDefinition : any, validationSchemaShape: any) {
      validationSchemaShape[formFieldDefinition.name] = Yup.mixed()
        .test(
          "fileSize",
          formFieldDefinition.maxUploadSizeKBError || "File too large",
          value => ((value && value.size <= formFieldDefinition.maxUploadSizeKB * 1024) || 
            !value || 
            !formFieldDefinition.maxUploadSizeKB)
        )
        .test(
          "fileFormat",
          formFieldDefinition.fileFormatsError || StringHelpers.formFieldUnsupportedFormatFieldErrorMessage(FormLabelHelpers.getLabelWithoutAsterisk(formFieldDefinition.label)) || "Unsupported Format",
          value => {
            let fileNameToSplit = "";
            if (typeof value !== "string") {
              fileNameToSplit = value.name || "";
            } else {
              fileNameToSplit = value;
            }
            
            const fileExtension = value ? FileNameHelpers.getFileNameExtensionUppercase(fileNameToSplit || "") : "";
            if (value == "") {
              return true;
            }
            return (value && ((formFieldDefinition.fileFormats || []).indexOf(fileExtension.toLowerCase()) >= 0) || 
              (formFieldDefinition.fileFormats || []).length == 0 || 
              !value
            );
          }
        );

      let isRequired = formFieldDefinition.validation['required'] !== undefined; 
      if (isRequired) {
        validationSchemaShape[formFieldDefinition.name] = validationSchemaShape[formFieldDefinition.name].test(
          "fileRequired",
          StringHelpers.formFieldRequiredFieldErrorMessage(FormLabelHelpers.getLabelWithoutAsterisk(formFieldDefinition.label)),
          value => {
            if (value === "") {
              return false;
            }
            return value ? value.name !== undefined && value.name != "" : false;
          }
        );
      }
    }

    static addMaxSizeValidator(formFieldDefinition : any, validationSchemaShape: any) {
      if (formFieldDefinition.maxSize) {
        validationSchemaShape[formFieldDefinition.name] = Yup.string().max(formFieldDefinition.maxSize, getResourceString("Form.Error.MaxLength") || formFieldDefinition.maxSizeError || "Maximale lengte overschreden");
      }
    }
}